import { useCallback, useEffect, useRef, useState } from "react";
import type { GuestRequest } from "@shared/types/guestRequest";
import { guestRequestIsAtRest } from "@shared/guestRequests";
import { getBinding } from "../lib/binding";
import { eventBus } from "../lib/eventBus";
import {
  offerErrorCode,
  type OfferErrorCode,
} from "../schemes/_shared/offers/offersCopy";
import { fetchBounded } from "../lib/boundedFetch";

/**
 * The one transaction a press on the television turns into (`POS-7` calling
 * `POS-6`).
 *
 * Three routes, all screen-token-only by design — a CMS session is refused
 * rather than accommodated, because the room a request is attributed to comes
 * from the screen's own binding and from nothing else (`docs/room-offers.md`
 * §11). A television nobody has placed in a room therefore cannot order, and
 * says so in words instead of guessing a room somebody would have to deliver to.
 *
 * ## The status flip is the server's, never ours
 *
 * `pending → accepted` arrives over the realtime hub as
 * `guest_request_changed`, published room-scoped by POS-6. Nothing here invents
 * it on a timer: a television that congratulated a guest before reception had
 * touched the row would be the exact lie `docs/room-offers.md` forbids
 * ("Receptionen bekräftar strax" is true; "Bokat" is not).
 *
 * The event rides `eventBus`, which `App.tsx` already pumps every realtime
 * event onto — one connection per television, shared by the door sign, the
 * message overlay and now this. Opening a second `connectRealtime` here would
 * give a hotel TV two sockets for one stream.
 *
 * A slow poll sits under it as a floor, for the same reason the command drain
 * grew one: a room with bad Wi-Fi can hold a socket that never delivers, and a
 * guest staring at "Skickar…" forever is worse than a confirmation that arrives
 * a few seconds late. It is deliberately slower than the hub is fast, so the
 * hub is still what the demo shows.
 */

/** How often to re-read our own request while it is still moving. */
const POLL_MS = 20_000;

/** What the guest picked, in the shape POS-6's route parses. */
export interface GuestRequestPayload {
  selection: Array<{ itemId: string; quantity: number }>;
  /** A LOCAL WALL CLOCK (`2026-08-20T19:00`), passed through untouched. */
  slotStart?: string;
  partySize?: number;
}

export type SendOutcome =
  | { ok: true; request: GuestRequest }
  | { ok: false; code: OfferErrorCode };

export interface GuestRequestHandle {
  /** The row this television raised, once there is one. */
  request: GuestRequest | null;
  /** True from the press until the server answers. */
  sending: boolean;
  /** The room this television is bound to, or null when it is not bound. */
  roomId: string | null;
  send: (
    offerId: string,
    payload: GuestRequestPayload,
    idempotencyKey: string
  ) => Promise<SendOutcome>;
  cancel: () => Promise<SendOutcome>;
}

export function useGuestRequest(): GuestRequestHandle {
  const [request, setRequest] = useState<GuestRequest | null>(null);
  const [sending, setSending] = useState(false);
  // Mirrors `request`, readable synchronously: the realtime listener and the
  // poll are both bound once and must see the CURRENT row, not the one that
  // existed when the effect ran.
  const requestRef = useRef<GuestRequest | null>(null);
  const binding = getBinding();
  const roomId = binding?.roomId ?? null;

  const remember = useCallback((next: GuestRequest | null) => {
    requestRef.current = next;
    setRequest(next);
  }, []);

  const target = useCallback((): {
    premiseId: string;
    /** The credential, and nothing else. */
    headers: Record<string, string>;
  } | null => {
    const b = getBinding();
    if (!b) return null;
    // Deliberately WITHOUT `content-type`: only one of the three calls carries a
    // body, and Fastify answers a bodiless POST that claims `application/json`
    // with `FST_ERR_CTP_EMPTY_JSON_BODY` — a 400 whose body has no `error`
    // code at all, so the television rendered "ring reception" over a cancel
    // that never reached the route. Measured on a real server, 2026-08-14.
    return {
      premiseId: b.premiseId,
      headers: { "X-Screen-Token": b.screenToken },
    };
  }, []);

  const send = useCallback(
    async (
      offerId: string,
      payload: GuestRequestPayload,
      idempotencyKey: string
    ): Promise<SendOutcome> => {
      const t = target();
      // No binding is not an error to swallow: there is no room to deliver to
      // and no folio to charge, and POS-6 answers exactly this code when a
      // paired-but-unplaced screen asks.
      if (!t) return { ok: false, code: "room_unknown" };
      setSending(true);
      try {
        // ONE attempt, then ONE retry with the SAME key, then the phone number.
        //
        // The key is what makes the retry safe: POS-6 keys idempotency on
        // `(premiseId, idempotencyKey)` and replays the original row with a 200,
        // so a request that landed but whose answer was lost comes back as
        // itself rather than as a second club sandwich. A third attempt would
        // add nothing a second did not — at that point the room's Wi-Fi is the
        // problem, and a guest needs a telephone, not another spinner.
        for (let attempt = 0; attempt < 2; attempt += 1) {
          try {
            const res = await fetchBounded(
              `/api/frontlayer/v1/premise/${t.premiseId}/offers/${offerId}/request`,
              {
                method: "POST",
                headers: { ...t.headers, "content-type": "application/json" },
                body: JSON.stringify({ ...payload, idempotencyKey }),
              }
            );
            if (res.ok) {
              const row = (await res.json()) as GuestRequest;
              remember(row);
              return { ok: true, request: row };
            }
            // A 5xx is the one refusal worth re-asking: it is the server
            // failing, not the request being wrong. Every 4xx carries a code
            // that says what the guest should do instead, and re-sending it
            // would produce the same answer a second later.
            if (res.status < 500) {
              const body = (await res.json().catch(() => ({}))) as {
                error?: unknown;
              };
              return { ok: false, code: offerErrorCode(body.error) };
            }
          } catch {
            // Network. Fall through to the retry, then to `unreachable`.
          }
        }
        return { ok: false, code: "unreachable" };
      } finally {
        setSending(false);
      }
    },
    [target, remember]
  );

  const cancel = useCallback(async (): Promise<SendOutcome> => {
    const t = target();
    const current = requestRef.current;
    if (!t) return { ok: false, code: "room_unknown" };
    if (!current) return { ok: false, code: "not_found" };
    setSending(true);
    try {
      const res = await fetchBounded(
        `/api/frontlayer/v1/premise/${t.premiseId}/offers/requests/${current.id}/cancel`,
        { method: "POST", headers: t.headers }
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: unknown;
        };
        return { ok: false, code: offerErrorCode(body.error) };
      }
      const row = (await res.json()) as GuestRequest;
      remember(row);
      return { ok: true, request: row };
    } catch {
      return { ok: false, code: "unreachable" };
    } finally {
      setSending(false);
    }
  }, [target, remember]);

  /** Re-read our own row. The poll's only job, and the event's confirmation. */
  const refresh = useCallback(async () => {
    const t = target();
    const current = requestRef.current;
    if (!t || !current) return;
    try {
      // `all=1`: the default list is open requests only, and a request that was
      // DECLINED while the guest watched would simply vanish from it — leaving
      // the screen saying "Receptionen bekräftar strax" about a row that is
      // never coming back.
      const res = await fetchBounded(
        `/api/frontlayer/v1/premise/${t.premiseId}/offers/requests/mine?all=1`,
        { headers: t.headers }
      );
      if (!res.ok) return;
      const rows = (await res.json()) as GuestRequest[];
      const mine = Array.isArray(rows)
        ? rows.find((r) => r.id === current.id)
        : undefined;
      if (mine) remember(mine);
    } catch {
      // Best-effort: the hub is the fast path and the next tick tries again.
    }
  }, [target, remember]);

  // The flip, over the hub. Room-scoped already, and re-checked here anyway:
  // the bus carries every event this television receives, and a request id from
  // another room would be somebody else's dinner.
  useEffect(() => {
    return eventBus.on((evt) => {
      if (evt.type !== "guest_request_changed") return;
      const current = requestRef.current;
      if (!current || evt.requestId !== current.id) return;
      if (evt.roomId !== current.roomId) return;
      // The event carries the new status, so the screen turns on the push
      // itself rather than on the round trip that follows it — that beat is
      // the whole point of pushing. The refresh behind it fills in the stamps.
      remember({ ...current, status: evt.status });
      void refresh();
    });
  }, [refresh, remember]);

  // The floor under the push.
  useEffect(() => {
    // Stops at REST rather than at final: a `completed` row's only remaining
    // exit is `refunded`, which is staff correcting the books days later and
    // not something the guest's open page is waiting for. The hub still pushes
    // it if it happens while they are looking.
    if (!request || guestRequestIsAtRest(request.status)) return;
    const timer = setInterval(() => void refresh(), POLL_MS);
    return () => clearInterval(timer);
  }, [request, refresh]);

  return { request, sending, roomId, send, cancel };
}
